<template>
  <i-box :selected="selected">
    <span slot="title">
      <div class="arrow arrow-left" @click="onClickPrevious">
        <i class="fa fa-arrow-circle-left"></i>
      </div>
      <div class="arrow arrow-right" @click="onClickNext">
        <i class="fa fa-arrow-circle-right"></i>
      </div>
      <div class="unit-title">
        <span class="label pull-right" :class="unitClass">{{ unit }}</span>
        <h5>{{ period }}</h5>
      </div>
    </span>

    <span v-if="processStats.length" slot="content">
      <h1 class="no-margins">{{ Math.round(received).toLocaleString() }}</h1>

      <div
        class="stat-percent font-bold"
        :class="{
          'text-success': growth > 0,
          'text-danger': growth < 0,
          'text-muted': growth === 0,
        }"
      >
        {{ Math.round(growth) }}%
        <i
          class="fa"
          :class="{
            'fa-level-up': growth > 0,
            'fa-level-down': growth < 0,
            'fa-minus': growth === 0,
          }"
        ></i>
      </div>

      <div v-if="showBreakdown" class="voice-breakdown">
        <span v-if="approved !== null" class="breakdown-badge breakdown-badge--approved">
          <i class="fa fa-check"></i> {{ approved }} approved
        </span>
        <span v-if="flagged !== null" class="breakdown-badge breakdown-badge--flagged">
          <i class="fa fa-flag"></i> {{ flagged }} flagged
        </span>
        <span v-if="pending !== null" class="breakdown-badge breakdown-badge--pending">
          <i class="fa fa-clock-o"></i> {{ pending }} pending
        </span>
      </div>

      <small class="description-text">{{ description }}</small>

      <div class="sparkline">
        <trend :data="processStats" :gradient="gradient" :height="40" auto-draw smooth></trend>
      </div>
    </span>

    <span v-else slot="content">
      <slot name="content-loader">
        <div class="voice-skeleton">
          <div class="skeleton-count"></div>
          <div class="skeleton-line skeleton-line--short"></div>
          <div class="skeleton-line skeleton-line--long"></div>
          <div class="skeleton-chart"></div>
        </div>
      </slot>
    </span>
  </i-box>
</template>

<script>
import Trend from 'vuetrend';
import IBox from '@/components/common/IBox';

export default {
  name: 'VoiceAnalyticsUnitBlock',

  components: {
    Trend,
    IBox,
  },

  props: {
    unit: {
      type: String,
      required: true,
    },
    period: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      default: 'Voice notes received',
    },
    received: {
      type: Number,
      default: 0,
    },
    growth: {
      type: Number,
      default: 0,
    },
    stats: {
      type: Array,
      default: () => [],
    },
    unitClass: {
      type: String,
      default: 'label-primary',
    },
    selected: {
      type: Boolean,
      default: true,
    },
    approved: {
      type: Number,
      default: null,
    },
    flagged: {
      type: Number,
      default: null,
    },
    pending: {
      type: Number,
      default: null,
    },
    gradient: {
      type: Array,
      default: () => ['#683090', '#c97eff'],
    },
  },

  computed: {
    showBreakdown() {
      return this.approved !== null || this.flagged !== null || this.pending !== null;
    },
    processStats() {
      return [0].concat(this.stats.map(stat => stat.total));
    },
  },

  methods: {
    onClickPrevious() {
      this.$emit('previous');
    },
    onClickNext() {
      this.$emit('next');
    },
  },
};
</script>

<style lang="scss" scoped>
.sparkline {
  height: 40px;
  margin-top: 4px;
}

.arrow {
  position: absolute;
  cursor: pointer;
  opacity: 0.4;
  top: 9px;
  font-size: 22px;
  transition: opacity 0.15s, color 0.15s;
}
.arrow:hover {
  color: #683090;
  opacity: 1;
}
.arrow-left {
  left: 20px;
}
.arrow-right {
  right: 20px;
}

.unit-title {
  margin: 0 20px;
}

.description-text {
  display: block;
  margin-top: 4px;
  margin-bottom: 2px;
  color: #888;
  font-size: 11px;
}

.voice-breakdown {
  display: flex;
  flex-wrap: nowrap;
  gap: 3px;
  margin: 3px 0 2px;
}

.breakdown-badge {
  font-size: 9px;
  font-weight: 600;
  padding: 1px 5px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  gap: 3px;

  &--approved {
    background: #e6f9f4;
    color: #1ab394;
  }
  &--flagged {
    background: #fdf0f0;
    color: #ed5565;
  }
  &--pending {
    background: #f5f0fb;
    color: #683090;
  }
}

.voice-skeleton {
  padding: 4px 0;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

%skeleton-base {
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}

.skeleton-count {
  @extend %skeleton-base;
  width: 70px;
  height: 36px;
  margin-bottom: 8px;
}

.skeleton-line {
  @extend %skeleton-base;
  height: 10px;
  margin-bottom: 6px;

  &--short {
    width: 40%;
  }
  &--long {
    width: 70%;
  }
}

.skeleton-chart {
  @extend %skeleton-base;
  height: 40px;
  margin-top: 8px;
  border-radius: 4px;
}

:deep(.ibox) {
  border-radius: 0 !important;
  overflow: hidden !important;
}

:deep(.ibox-content) {
  padding: 10px 12px !important;
}
</style>


// WEBPACK FOOTER //
// VoiceAnalyticsUnitBlock.vue?a2ee598c